import * as db from '$lib/db';
import { enrich } from '$lib/enrich';
import type { Site, SiteRow } from '$lib/types';
import { filters } from './filters.svelte';

const CACHE_KEY = 'ph_sites_cache_v2';
const LEGACY_CACHE_KEY = 'ph_sites_cache';
const MAX_AGE_MS = 24 * 3600 * 1000;

let raw = $state<SiteRow[]>([]);
let loaded = $state(false);
let loadError = $state<unknown>(null);
let offlineTs = $state<string | null>(null);
let lastSyncTs = $state<number | null>(null);
let cachedTs: string | null = null;
// Roof overrides keyed by site_id — drives the RoofLayer (kept separate from
// enrich, since get_sites_live already returns override-adjusted roof area).
let overrides = $state<Record<string, db.RoofOverride>>({});

const all = $derived(raw.map((r, i) => enrich(r, i)));

const filtered = $derived.by(() => {
  const q = filters.search.trim().toLowerCase();
  return all
    .filter((s) => {
      if (filters.preset === 'high_confidence' && (s.confidence == null || s.confidence < 70)) return false;
      if (filters.preset === 'near_cabinet' && !s.hasCabinet && !s.assetList.includes('cabinet')) return false;
      if (filters.asset !== 'all' && !s.assetList.includes(filters.asset)) return false;
      if (filters.phase !== 'all' && s.phase !== filters.phase) return false;
      if (s.area < filters.minArea) return false;
      if (filters.highOnly && s.suitLabel !== 'High') return false;
      if (filters.noRoof && !s.noRoof) return false;
      if (q && !(s.name.toLowerCase().includes(q) || (s.sub || '').toLowerCase().includes(q)))
        return false;
      return true;
    })
    .sort((a, b) => {
      if (filters.preset === 'high_confidence') {
        return (b.confidence - a.confidence) || (b.kwp - a.kwp);
      }
      if (filters.preset === 'max_capacity' || filters.preset === 'near_cabinet') {
        return b.kwp - a.kwp;
      }
      // 'balanced'
      const scoreB = b.kwp * (b.confidence > 0 ? b.confidence / 100 : 0.5);
      const scoreA = a.kwp * (a.confidence > 0 ? a.confidence / 100 : 0.5);
      return scoreB - scoreA || b.kwp - a.kwp;
    });
});

export const sites = {
  get all() {
    return all;
  },
  get filtered() {
    return filtered;
  },
  get loaded() {
    return loaded;
  },
  get loadError() {
    return loadError;
  },
  get offlineTs() {
    return offlineTs;
  },
  get lastSyncTs() {
    return lastSyncTs;
  },
  get overrides() {
    return overrides;
  },

  byId(id: string | null): Site | undefined {
    if (!id) return undefined;
    return all.find((s) => s.vid === id || s.id === id);
  },

  /** Remove a site from the local list (optimistic). Returns the removed row for rollback. */
  removeLocally(vid: string): SiteRow | undefined {
    const row = raw.find((r) => r.id === vid);
    raw = raw.filter((r) => r.id !== vid);
    return row;
  },

  /** Re-add a previously removed site (rollback on delete failure). */
  restoreLocally(row: SiteRow): void {
    raw = [...raw, row];
  },

  /**
   * Drop every loaded row and the on-device cache. Called on sign-out — the
   * app is auth-only, so nothing survey-related should outlive the session on
   * a shared machine.
   */
  reset(): void {
    raw = [];
    overrides = {};
    loaded = false;
    loadError = null;
    offlineTs = null;
    lastSyncTs = null;
    cachedTs = null;
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(LEGACY_CACHE_KEY);
    } catch {
      /* ignore */
    }
  },

  /** Restore the site list from the 24h localStorage cache before fetching fresh. */
  hydrate(): void {
    try {
      // Never carry a positional tuple cache across the transport migration.
      localStorage.removeItem(LEGACY_CACHE_KEY);
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return;
      const parsed = JSON.parse(cached) as { schema?: unknown; timestamp?: unknown; data?: unknown };
      const { schema, timestamp, data } = parsed;
      if (schema !== 2 || typeof timestamp !== 'string' || !Array.isArray(data)) {
        localStorage.removeItem(CACHE_KEY);
        return;
      }
      if (!data.length) return;
      const timestampMs = new Date(timestamp).getTime();
      if (!Number.isFinite(timestampMs) || Date.now() - timestampMs > MAX_AGE_MS) {
        localStorage.removeItem(CACHE_KEY);
        return;
      }
      raw = data as SiteRow[];
      loaded = true;
      cachedTs = timestamp;
      lastSyncTs = timestampMs;
    } catch {
      /* ignore malformed cache */
    }
  },

  /** Fetch fresh sites from get_sites_live; fall back to cache on failure. */
  async load(): Promise<void> {
    try {
      const rows = await db.getSitesLive();
      raw = rows;
      loaded = true;
      loadError = null;
      offlineTs = null;
      lastSyncTs = Date.now();
      cachedTs = null;
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ schema: 2, timestamp: new Date().toISOString(), data: rows }),
        );
      } catch {
        /* quota */
      }
    } catch (err) {
      console.warn('sites.load failed, using cache if present:', err);
      loadError = err;
      loaded = true;
      if (cachedTs) offlineTs = cachedTs;
    }
  },

  /** Pull all roof overrides (initial) and merge them. */
  async loadOverrides(): Promise<void> {
    try {
      const rows = await db.getOverrides();
      const map: Record<string, db.RoofOverride> = {};
      for (const ro of rows) map[ro.site_id] = ro;
      overrides = map;
    } catch (err) {
      console.warn('loadOverrides failed (non-fatal):', err);
    }
  },

  /** Apply a single override (realtime push). */
  setOverride(ro: db.RoofOverride): void {
    overrides = { ...overrides, [ro.site_id]: ro };
  },
  removeOverride(siteId: string): void {
    const next = { ...overrides };
    delete next[siteId];
    overrides = next;
  },
};
